/**
 * SalesWorkflows — Workflow list page with cards, toggle, delete.
 *
 * @page /sales/workflows
 * @roles admin, manager
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { ToggleSwitch } from '../../components/ui';
import {
  GitBranch, Plus, Trash2, ArrowRight, Clock, Mail,
  CheckSquare, Zap, Edit3, Copy,
} from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

function api(path, opts = {}) {
  const hasBody = opts.body != null;
  const headers = hasBody ? { 'Content-Type': 'application/json' } : {};
  return fetch(`${API}${path}`, {
    ...opts, credentials: 'include',
    headers: { ...headers, ...opts.headers },
    body: hasBody ? JSON.stringify(opts.body) : undefined,
  }).then(async (r) => {
    const data = await r.json();
    if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
    return data;
  });
}

const STEP_TYPES = {
  delay:     { label: 'delay',     icon: Clock,       color: '#F59E0B' },
  email:     { label: 'email',     icon: Mail,        color: '#338edc' },
  task:      { label: 'task',      icon: CheckSquare, color: '#10B981' },
  stage:     { label: 'stage',     icon: ArrowRight,  color: '#6366F1' },
  field:     { label: 'field',     icon: Edit3,       color: '#22D3EE' },
  webhook:   { label: 'webhook',   icon: Zap,         color: '#EF4444' },
  condition: { label: 'condition', icon: GitBranch,   color: '#F97316' },
};

function countSteps(steps) {
  let count = 0;
  for (const step of (steps || [])) {
    count++;
    if (step.yes) count += countSteps(step.yes);
    if (step.no) count += countSteps(step.no);
  }
  return count;
}

function getStepTypeSummary(steps) {
  const counts = {};
  function walk(arr) {
    for (const s of (arr || [])) {
      counts[s.type] = (counts[s.type] || 0) + 1;
      if (s.yes) walk(s.yes);
      if (s.no) walk(s.no);
    }
  }
  walk(steps);
  return counts;
}

export default function SalesWorkflows() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => { loadWorkflows(); }, []);

  async function loadWorkflows() {
    setLoading(true);
    try {
      const data = await api('/api/sales/workflow-builder');
      setWorkflows(Array.isArray(data) ? data : []);
    } catch { setWorkflows([]); }
    setLoading(false);
  }

  async function toggleWorkflow(e, id) {
    if (e?.stopPropagation) e.stopPropagation();
    try {
      await api(`/api/sales/workflow-builder/${id}/toggle`, { method: 'PUT' });
      loadWorkflows();
    } catch (e) { console.error(e); }
  }

  async function deleteWorkflow() {
    if (!deleteModal) return;
    try {
      await api(`/api/sales/workflow-builder/${deleteModal}`, { method: 'DELETE' });
      setDeleteModal(null);
      loadWorkflows();
    } catch (e) { console.error(e); }
  }

  async function duplicateWorkflow(e, id) {
    if (e?.stopPropagation) e.stopPropagation();
    try {
      const wf = await api(`/api/sales/workflow-builder/${id}/duplicate`, { method: 'POST' });
      if (wf?.id) navigate(`/sales/workflows/${wf.id}`);
    } catch (err) { console.error(err); }
  }

  async function createWorkflow() {
    setCreating(true);
    try {
      const wf = await api('/api/sales/workflow-builder', {
        method: 'POST',
        body: { name: 'Neuer Workflow', trigger_type: 'stage_change', steps: [] }
      });
      if (wf.id) navigate(`/sales/workflows/${wf.id}`);
    } catch (e) { console.error(e); }
    setCreating(false);
  }

  return (
    <div className="p-8">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-[22px] font-semibold text-[var(--text-primary)]">
            {t('workflows.title')}
          </h1>
          <p className="text-[13px] text-[var(--text-secondary)] mt-1">
            {t('workflows.subtitle')}
          </p>
        </div>
        <button
          onClick={createWorkflow}
          disabled={creating}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#338edc] text-white text-[13px] font-semibold rounded-[10px] hover:bg-[#2b7bc4] disabled:opacity-50 transition-colors"
        >
          <Plus className="w-4 h-4" strokeWidth={1.5} />
          {t('workflows.newWorkflow')}
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-[13px] text-[var(--text-tertiary)] text-center py-12">Loading...</div>
      ) : workflows.length === 0 ? (
        <div className="text-center py-16 bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl">
          <GitBranch className="w-10 h-10 mx-auto mb-3 text-[var(--text-tertiary)]" strokeWidth={1} />
          <div className="text-[13px] font-medium text-[var(--text-secondary)]">
            {t('workflows.empty')}
          </div>
          <div className="text-[12px] text-[var(--text-tertiary)] mt-1 mb-4">
            {t('workflows.emptyDesc')}
          </div>
          <button
            onClick={createWorkflow}
            disabled={creating}
            className="px-4 py-2 bg-[#338edc] text-white text-[13px] font-semibold rounded-[10px] hover:bg-[#2b7bc4] disabled:opacity-50 transition-colors"
          >
            <Plus className="w-3.5 h-3.5 inline mr-1" strokeWidth={1.5} />
            {t('workflows.newWorkflow')}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {workflows.map(wf => {
            const stepCount = countSteps(wf.steps || []);
            const typeSummary = getStepTypeSummary(wf.steps || []);

            return (
              <div
                key={wf.id}
                onClick={() => navigate(`/sales/workflows/${wf.id}`)}
                className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl p-5 cursor-pointer hover:border-[var(--border-default,#E5E7EB)] dark:hover:border-gray-600 transition-all group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    {/* Name + Status */}
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[14px] font-bold text-[var(--text-primary)]">{wf.name}</span>
                      {wf.is_active ? (
                        <span className="text-[10px] font-semibold text-green-700 bg-green-50 dark:bg-green-900/20 dark:text-green-400 px-2 py-0.5 rounded-full">
                          {t('workflows.active')}
                        </span>
                      ) : (
                        <span className="text-[10px] font-semibold text-[var(--text-tertiary)] bg-[var(--bg-inset)] px-2 py-0.5 rounded-full">
                          {t('workflows.inactive')}
                        </span>
                      )}
                    </div>

                    {/* Meta */}
                    <div className="text-[11px] text-[var(--text-secondary)] mb-2">
                      {wf.segment && <span>Segment: {wf.segment} · </span>}
                      <span>Trigger: {t(`workflowEditor.triggers.${wf.trigger_type}`) || wf.trigger_type} · </span>
                      <span>{stepCount} {stepCount === 1 ? t('workflows.step') : t('workflows.steps')}</span>
                      {wf.active_executions > 0 && (
                        <span className="text-[#338edc] ml-1">
                          · {wf.active_executions} {t('workflows.active').toLowerCase()}
                        </span>
                      )}
                    </div>

                    {/* Step-Type Mini-Badges */}
                    {stepCount > 0 && (
                      <div className="flex gap-1 flex-wrap">
                        {Object.entries(typeSummary).map(([type, count]) => {
                          const st = STEP_TYPES[type];
                          if (!st) return null;
                          const Icon = st.icon;
                          return (
                            <span
                              key={type}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-medium"
                              style={{ background: `${st.color}10`, color: st.color }}
                            >
                              <Icon className="w-2.5 h-2.5" strokeWidth={1.5} />
                              {count}× {t(`workflowEditor.stepTypes.${st.label}`)}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-3 ml-4" onClick={e => e.stopPropagation()}>
                    {/* Toggle */}
                    <ToggleSwitch
                      checked={wf.is_active}
                      onChange={() => toggleWorkflow(null, wf.id)}
                    />

                    {/* Duplicate */}
                    <button
                      onClick={(e) => duplicateWorkflow(e, wf.id)}
                      title={t('workflows.duplicate')}
                      className="p-1.5 rounded-lg text-[var(--text-tertiary)] hover:text-[#338edc] hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <Copy className="w-4 h-4" strokeWidth={1.5} />
                    </button>

                    {/* Delete */}
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeleteModal(wf.id); }}
                      title={t('workflows.delete')}
                      className="p-1.5 rounded-lg text-[var(--text-tertiary)] hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>

                    {/* Arrow */}
                    <ArrowRight className="w-4 h-4 text-[var(--text-tertiary)] group-hover:text-[#338edc] transition-colors" strokeWidth={1.5} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Modal */}
      {deleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={() => setDeleteModal(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border-card)] shadow-[var(--shadow-modal)] w-full max-w-sm p-6">
            <div className="text-[15px] font-semibold text-[var(--text-primary)] mb-2">
              {t('workflows.deleteTitle')}
            </div>
            <p className="text-[13px] text-[var(--text-secondary)] mb-1">
              {t('workflows.deleteDesc')}
            </p>
            {(() => {
              const wf = workflows.find(w => w.id === deleteModal);
              return wf ? (
                <div className="text-[13px] font-medium text-[var(--text-primary)] bg-[var(--bg-inset)] px-3 py-2 rounded-lg mt-2 mb-4">
                  "{wf.name}"
                  {wf.active_executions > 0 && (
                    <div className="text-[11px] text-amber-600 dark:text-amber-400 mt-1">
                      ⚠ {wf.active_executions} {t('workflows.activeExecWarning')}
                    </div>
                  )}
                </div>
              ) : null;
            })()}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteModal(null)}
                className="px-4 py-2 text-[13px] font-medium border border-[var(--border-card)] rounded-[10px] text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover)] transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={deleteWorkflow}
                className="px-4 py-2 text-[13px] font-semibold text-white bg-red-500 rounded-[10px] hover:bg-red-600 transition-colors"
              >
                {t('workflows.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
